import {useCallback, useEffect, useRef, useState} from "react";
import {currencyForPricingCountry, pricingCountryForContext} from "pricing/planCatalogue";
import {useLocale} from "i18n";
import {storefrontLocale} from "i18n/localePreference";
import {useAuthenticatedSession, useUser} from "UserProvider";
import {parsePromoCode, promoAudience, promoDialogOpen, promoPlatformName} from "./promoLink";
import {PromoOfferApi, type PromoGift} from "./PromoOfferApi";

/** What the promo-link dialog mount needs to know, and nothing a view could misuse. */
export interface PromoLinkOfferState {
	/** The verified offer for the arriving code, or null while there is nothing to show. */
	offer: PromoGift | null;
	/** Allowlisted display name of the platform the link named ("Reddit"), or null for generic copy. */
	platform: string | null;
	/** The dialog is on screen. Stays mounted through checkout; drives the exit transition on close. */
	open: boolean;
	/**
	 * Whether this visit still wants the offer, read at the moment an async continuation resumes —
	 * the sign-in a guest Subscribe raises outlives any single render, and a checkout must never
	 * start for a dialog the reader closed while the modal was up.
	 */
	activeVisit: () => boolean;
	/** The ✕, the scrim and Escape. A this-visit fact only: nothing is stored (ui#2343). */
	dismiss: () => void;
}

/**
 * The coupon offer a visitor meets on a promo link (`?promo=CODE`), on any acquisition platform
 * (kitecorp/transferify-ui#2343).
 *
 * A thin React adapter by mandate: admission is `./promoLink`'s (`promoAudience`, `promoDialogOpen`)
 * and the lookup is `./PromoOfferApi`'s. This holds the browser facts those rules are asked about —
 * the code in the URL, the resolved offer, this visit's dismissal — and nothing else.
 *
 * <b>The query string is read once after mount</b>, the same ordering `useReferralLandingOffer`
 * gives for the same read: it is invisible to the SSG prerender, so a render that consulted it
 * would diverge from the pre-rendered HTML (ui#74/#418).
 *
 * <b>One lookup per visit, never cached, never retried.</b> Opening the link consumes no redemption,
 * so a later visit simply asks again; retrying inside one visit would turn the api's null — which
 * covers provider failure and throttling too — into a polling loop for an offer it already declined
 * to advertise.
 */
export function usePromoLinkOffer(): PromoLinkOfferState {
	const {locale} = useLocale();
	const [user] = useUser();
	const [session] = useAuthenticatedSession();
	const [code, setCode] = useState<string | null>(null);
	const [platform, setPlatform] = useState<string | null>(null);
	const [offer, setOffer] = useState<PromoGift | null>(null);
	const [dismissed, setDismissed] = useState(false);
	useEffect(() => {
		setCode(parsePromoCode(window.location.search));
		setPlatform(promoPlatformName(window.location.search));
	}, []);
	// Admission gates the LOOKUP only: a paid subscriber's browser never even asks. The latch keeps
	// the session settling (and every later re-render) from asking twice within one visit.
	const asked = useRef(false);
	const audience = promoAudience(user, session);
	useEffect(() => {
		if (code === null || !audience || asked.current) {
			return;
		}
		asked.current = true;
		// The money the dialog will display and charge in: the storefront's, resolved by the same rule
		// the checkout itself applies, so the quoted credit and the charged currency cannot differ.
		const currency = currencyForPricingCountry(pricingCountryForContext(storefrontLocale(), window.location.hostname));
		void PromoOfferApi.resolve(code, currency).then(setOffer);
		// Keyed on the reader's LANGUAGE, which a switch moves, so the answer is re-fetched for the
		// catalogue they have just chosen. The VALUE used is read live inside the effect (above), so the
		// dependency is a change signal rather than the input — `setStoredLocale` refreshes the cache the
		// live read consults before this re-runs.
	}, [code, audience, locale]);
	const open = promoDialogOpen(user, offer, dismissed);
	const openRef = useRef(open);
	openRef.current = open;
	const activeVisit = useCallback(() => openRef.current, []);
	const dismiss = useCallback(() => setDismissed(true), []);
	return {offer, platform, open, activeVisit, dismiss};
}
